import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Recycle, ArrowRight, ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HEADLINE_LINES = ["Secure IT Asset Disposal", "& E-Waste Recycling", "for Australian Businesses."];

const EASE = [0.16, 1, 0.3, 1];

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: EASE, delay: 0.35 + i * 0.12 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-60%"]);

  return (
    <section
      id="home"
      ref={ref}
      className="noise-overlay relative flex min-h-screen items-center overflow-hidden bg-navy pt-28 pb-16 lg:pt-24"
      data-testid="hero-section"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 h-[36rem] w-[36rem] rounded-full bg-eco/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-0 select-none font-display text-[22vw] font-black leading-none text-outline"
      >
        ITAD
      </div>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.25 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-eco/30 bg-eco/10 px-4 py-1.5"
            data-testid="hero-overline"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-eco-bright" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-eco">
              Australia's Corporate ITAD Partner
            </span>
          </motion.div>

          <h1
            className="font-display text-4xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl"
            data-testid="hero-headline"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineReveal}
                  initial="hidden"
                  animate="visible"
                >
                  {i === 2 ? (
                    <>
                      for Australian <span className="text-eco">Businesses.</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
            data-testid="hero-subheadline"
          >
            Protect your corporate data and meet ESG targets with our compliant, zero-landfill
            e-waste collection service. Free collections available for qualifying bulk IT loads.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => scrollToId("#book")}
              data-testid="hero-book-cleanout-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-eco px-7 py-4 text-sm font-bold text-navy shadow-[0_8px_32px_rgba(52,199,123,0.4)] transition-[background-color,box-shadow] duration-300 hover:bg-eco-bright hover:shadow-[0_8px_44px_rgba(52,199,123,0.55)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eco"
            >
              Book a Free Office Cleanout
              <ArrowRight className="h-4 w-4" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => scrollToId("#services")}
              data-testid="hero-buyback-button"
              className="inline-flex items-center gap-2.5 rounded-full border border-white/25 px-7 py-4 text-sm font-bold text-white transition-[border-color,background-color] duration-300 hover:border-eco/60 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eco"
            >
              View Our Buyback Program
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-white/10 pt-7"
            data-testid="hero-stats"
          >
            {[
              ["2,400+", "assets processed"],
              ["100%", "zero landfill"],
              ["24h", "response time"],
            ].map(([num, label]) => (
              <div key={label}>
                <p className="font-display text-2xl font-semibold text-white sm:text-3xl">{num}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.15em] text-slate-400">{label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-6">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.3, ease: EASE, delay: 0.5 }}
            className="relative overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_80px_rgba(0,0,0,0.5)]"
            data-testid="hero-image-frame"
          >
            <motion.img
              src="/images/hero-server-room.png"
              alt="Modern corporate server room prepared for secure IT asset disposal"
              style={{ y: imageY, scale: 1.18 }}
              className="h-[24rem] w-full object-cover sm:h-[30rem] lg:h-[34rem]"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-navy/30 mix-blend-multiply" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-navy/80 to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.25 }}
            style={{ y: cardY }}
            className="absolute -bottom-6 -left-4 z-10 flex items-center gap-3 rounded-xl border border-white/10 bg-navy-800/80 p-4 shadow-2xl backdrop-blur-xl sm:-left-8"
            data-testid="hero-badge-destruction"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-eco/15">
              <ShieldCheck className="h-5 w-5 text-eco" />
            </span>
            <div>
              <p className="text-sm font-bold text-white">Certificate of Destruction</p>
              <p className="text-xs text-slate-400">Issued on every job</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.45 }}
            className="absolute -top-5 right-4 z-10 flex items-center gap-2.5 rounded-full border border-eco/30 bg-navy/80 px-4 py-2.5 shadow-xl backdrop-blur-xl"
            data-testid="hero-badge-landfill"
          >
            <Recycle className="h-4 w-4 animate-[spin_6s_linear_infinite] text-eco" />
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-eco">
              Zero Landfill
            </span>
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        onClick={() => scrollToId("#services")}
        aria-label="Scroll to services"
        data-testid="hero-scroll-indicator"
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 text-slate-400 transition-colors hover:text-eco lg:block"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.button>
    </section>
  );
}
